import { useEffect, useState } from "react";
import { Button } from "zudoku/components";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "zudoku/ui/Dialog";
import { Input } from "zudoku/ui/Input";
import { Label } from "zudoku/ui/Label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "zudoku/ui/Select";
import { Secret } from "zudoku/ui/Secret";
import { ClipboardCopy, Check } from "zudoku/icons";
import type { ApiKeyExpiryOption } from "../types/apiManagement";

interface CreateKeyDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSubmit: (values: {
		keyName: string;
		expiry: ApiKeyExpiryOption;
	}) => Promise<void> | void;
	isSubmitting?: boolean;
	appName?: string;
	createdKey?: {
		name: string;
		value: string;
	};
	onReset?: () => void;
}

const EXPIRY_OPTIONS: ApiKeyExpiryOption[] = [30, 180, 365];

export const CreateKeyDialog = ({
	open,
	onOpenChange,
	onSubmit,
	isSubmitting,
	appName,
	createdKey,
	onReset,
}: CreateKeyDialogProps) => {
	const [keyName, setKeyName] = useState("");
	const [expiry, setExpiry] = useState<ApiKeyExpiryOption>(EXPIRY_OPTIONS[1]);
	const [copied, setCopied] = useState(false);

	useEffect(() => {
		if (open && !createdKey) {
			setKeyName("");
			setExpiry(EXPIRY_OPTIONS[1]);
			setCopied(false);
		}
	}, [createdKey, open]);

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		await onSubmit({ keyName: keyName.trim(), expiry });
	};

	const handleClose = (nextOpen: boolean) => {
		if (!nextOpen && onReset) {
			onReset();
		}
		onOpenChange(nextOpen);
	};

	const handleCopy = async () => {
		if (!createdKey?.value) return;
		try {
			await navigator.clipboard.writeText(createdKey.value);
			setCopied(true);
			setTimeout(() => setCopied(false), 2500);
		} catch (error) {
			console.error("Failed to copy API key", error);
		}
	};

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-xl">
				{createdKey ? (
					<div className="space-y-4">
						<DialogHeader>
							<DialogTitle>API Key Created</DialogTitle>
							<DialogDescription>
								This key will only be shown once. Copy and store it securely.
							</DialogDescription>
						</DialogHeader>

						<div className="space-y-2">
							<Label>Key Name</Label>
							<p className="rounded-md border bg-muted px-3 py-2 text-sm">
								{createdKey.name}
							</p>
						</div>

						<div className="space-y-2">
							<Label>API Key</Label>
							<Secret secret={createdKey.value} preview={6} />
							<Button
								type="button"
								variant="outline"
								className="inline-flex items-center gap-2"
								onClick={handleCopy}
							>
								{copied ? (
									<>
										<Check size={16} />
										Copied
									</>
								) : (
									<>
										<ClipboardCopy size={16} />
										Copy to clipboard
									</>
								)}
							</Button>
						</div>

						<DialogFooter>
							<Button type="button" onClick={() => handleClose(false)}>
								Done
							</Button>
						</DialogFooter>
					</div>
				) : (
					<form onSubmit={handleSubmit} className="space-y-4">
						<DialogHeader>
							<DialogTitle>Create API Key</DialogTitle>
							<DialogDescription>
								Create a new key for{" "}
								<span className="font-medium">{appName ?? "this app"}</span>.
							</DialogDescription>
						</DialogHeader>

						<div className="space-y-2">
							<Label htmlFor="key-name">Key Name</Label>
							<Input
								id="key-name"
								required
								placeholder="Production integration"
								value={keyName}
								onChange={(event) => setKeyName(event.target.value)}
							/>
						</div>

						<div className="space-y-2">
							<Label htmlFor="key-expiry">Expiry</Label>
							<Select
								value={String(expiry)}
								onValueChange={(value) =>
									setExpiry(Number(value) as ApiKeyExpiryOption)
								}
							>
								<SelectTrigger id="key-expiry">
									<SelectValue placeholder="Select expiry" />
								</SelectTrigger>
								<SelectContent>
									{EXPIRY_OPTIONS.map((option) => (
										<SelectItem key={option} value={String(option)}>
											{option} days
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => handleClose(false)}
								disabled={isSubmitting}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={!keyName.trim() || isSubmitting}>
								{isSubmitting ? "Creating..." : "Create key"}
							</Button>
						</DialogFooter>
					</form>
				)}
			</DialogContent>
		</Dialog>
	);
};
